<template>
  <div class="c-sharing-settings-item max-w-full">
    <div class="flex items-center gap-10 max-w-[50%]">
      <BittsSvg
        :svg="
          dataShare.integrationType ? `${dataShare.integrationType}Icon` : ''
        "
        class="w-16 h-16 mr-2 min-w-[16px]"
      />
      <BittsTooltip :class="isForPartner ? 'max-w-[240px]' : 'max-w-[50%]'">
        <h3 class="font-bold truncate"> {{ dataShare.populationName }} </h3>
        <template #title>{{ dataShare.populationName }}</template>
      </BittsTooltip>
      <SharingSettingsVisibilitySelector
        :initial-visibility="selectedSharing"
        :disabled="disabled"
        :is-greenfield-sharing-blocked="isGreenfieldSharingBlocked"
        :disabled-option-content="{
          text: 'This sharing option was disabled. Please reach out to your admins.',
        }"
        @change="
          (visibility: SharingType) => {
            selectedSharing = visibility;
            handleChange({
              visibility,
              presetId: selectedPresetId ?? undefined,
            });
          }
        "
      />
    </div>
    <div class="flex items-center gap-16 max-w-[50%]">
      <SharingSettingsPresetSelector
        v-if="!isOverlapCounts"
        :initial-selected-preset-id="selectedPresetId ?? null"
        :preset-options="presetOptionsWithCustomFields"
        :disabled="disabled"
        :can-edit-presets="!isForPartner"
        :feed-id="actualFeedId"
        :source-id="sourceIdParam"
        @change="
          (presetId: number) =>
            handleChange({ visibility: selectedSharing, presetId })
        "
        @open-preset-modal="handleOpenPresetModal"
      />
      <template v-if="!disabled && isForPartner">
        <BittsTooltip>
          <template #title>
            {{
              isDefaultSettings
                ? 'This partner uses your default sharing settings for this Population.'
                : `You've customized sharing for this partner. Changes to your default settings won't apply here.`
            }}
          </template>
          <BittsTag type="neutral" size="small" class="min-w-[125px]">
            {{ isDefaultSettings ? 'Default Settings' : 'Custom Settings' }}
          </BittsTag>
        </BittsTooltip>
        <BittsDropdown
          :class="{ open: showDropdownContent }"
          :options
          placement="bottomRight"
          @selection="onOptionSelected"
        >
          <template #prompt>
            <BittsButton
              aria-label="More options"
              :center-icon="['fas', 'ellipsis']"
              size="x-small"
              type="neutral"
              variant="outline"
              @click="showDropdownContent = !showDropdownContent"
            />
          </template>
        </BittsDropdown>
      </template>
      <div v-if="!isForPartner || disabled">
        <BittsTooltip v-if="!disabled">
          <template #title>Unshare Population</template>
          <BittsButton
            @click="handleChange({ visibility: SHARING_TYPES.HIDDEN })"
            variant="outline"
            type="neutral"
            size="x-small"
            class="p-6"
          >
            <template #icon>
              <FontAwesomeIcon
                :icon="['fak', 'x']"
                :style="{
                  height: '12px',
                  width: '12px',
                  color: 'currentColor',
                }"
                class="text-danger-accent"
              />
            </template>
          </BittsButton>
        </BittsTooltip>
        <BittsButton
          v-else
          aria-label="View sharing details"
          class="c-sharing-settings-item__preset-details-btn"
          variant="ghost"
          type="neutral"
          size="small"
          :center-icon="getPresetSuffixIcon(Number(selectedPresetId))"
          @click="handleOpenPresetModal(Number(selectedPresetId))"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsDropdown,
  BittsSvg,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { BittsIconPrefix } from '@crossbeam/bitts/src/types';

import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import SharingSettingsPresetSelector from '@/components/sharing-settings/SharingSettingsPresetSelector.vue';
import SharingSettingsVisibilitySelector from '@/components/sharing-settings/SharingSettingsVisibilitySelector.vue';

import useAuth from '@/composables/useAuth';
import { useFieldPresetsModal } from '@/composables/useFieldPresetsModal';
import usePopulations from '@/composables/usePopulations';
import { SHARING_TYPES, SharingType } from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { SHARING_OVERRIDES_ENABLED } from '@/constants/feature_flags';
import {
  useDataSharesStore,
  useFeatureFlagStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';
import {
  DataSharePreset,
  PresetOptionsGroup,
} from '@/types/data_share_presets';

import { DataShareItem } from '../composables/useSharingSettings';

interface DropdownOption {
  value: string;
  display: string;
  classes?: string[];
  disabled?: boolean;
  tooltip?: string;
  iconType?: [BittsIconPrefix, string];
}

const {
  dataShare,
  presetOptions,
  singleSharePresets,
  createDataShare,
  updateDataShare,
  resetDataShare,
  defaultSharing,
  hasUnsavedChanges = false,
} = defineProps<{
  dataShare: DataShareItem;
  presetOptions?: PresetOptionsGroup[];
  singleSharePresets: DataSharePreset[];
  createDataShare: (params: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) => void;
  updateDataShare: (params: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) => void;
  resetDataShare: (params: DataShareItem) => void;
  defaultSharing?: DataShareItem;
  hasUnsavedChanges?: boolean;
}>();

const emit = defineEmits<(e: 'show-pop-overrides-modal') => void>();
const route = useRoute();
const { hasPermission } = useAuth();

const showDropdownContent = ref(false);

const { openModal } = useFieldPresetsModal();

const ffStore = useFeatureFlagStore();
const sourcesStore = useSourcesStore();
const hasSharingOverrides = computed(() =>
  ffStore.hasFeatureFlag(SHARING_OVERRIDES_ENABLED),
);

const dataSharesStore = useDataSharesStore();
const { isGreenfieldSharingBlocked } = storeToRefs(dataSharesStore);

const hasSharingPermission = computed(() => hasPermission('manage:sharing'));
const hasPopulationsPermission = computed(() =>
  hasPermission('manage:populations'),
);

const selectedSharing = ref(dataShare.visibility);
const selectedPresetId = computed(() => dataShare.presetId);

watch(
  () => dataShare.visibility,
  (visibility) => {
    selectedSharing.value = visibility;
  },
);

const isOverlapCounts = computed(
  () => selectedSharing.value === SHARING_TYPES.OLAPS_ONLY,
);

const isFromInviteOnboarding = computed(() =>
  isRouteNameInMatched('onboarding_accept_proposal'),
);
const isFromInvitePartners = computed(() =>
  isRouteNameInMatched('accept_proposal'),
);
const isFromPendingRequests = computed(
  () =>
    isRouteNameInMatched('pending_requests') ||
    isRouteNameInMatched('partner_wishlist_accept_proposal') ||
    isRouteNameInMatched('partner_wishlist_requests_accept_proposal') ||
    isRouteNameInMatched('sharing_settings_requests_accept_proposal'),
);
const isFromPartnerSettings = computed(() =>
  isRouteNameInMatched('partnership-settings'),
);
const isForPartner = computed(
  () =>
    isFromPartnerSettings.value ||
    isFromInvitePartners.value ||
    isFromPendingRequests.value ||
    isFromInviteOnboarding.value,
);

const disabled = computed(() => !hasSharingPermission.value);

const isDefaultSettings = computed(() => dataShare.partnerId === null);
const hasDefaultSettings = computed(
  () =>
    defaultSharing !== undefined &&
    defaultSharing.visibility !== SHARING_TYPES.HIDDEN,
);

const selectedSingleSharePreset = computed(() =>
  singleSharePresets.find((preset) => preset.id === selectedPresetId.value),
);

const presetOptionsWithCustomFields = computed(() => {
  const baseOptions = presetOptions ?? [];

  // If a single share preset is selected, add it to the options as "Custom Field Selection"
  if (!selectedSingleSharePreset.value) return baseOptions;

  return [
    ...baseOptions,
    {
      heading: null,
      items: [
        {
          value: selectedSingleSharePreset.value.id,
          label: 'Custom Field Selection',
          presetType: 'single_share_preset',
        },
      ],
    },
  ];
});

const { partnerPopulations } = storeToRefs(usePopulationsStore());
const partnerId = computed(() => {
  const paramPartnerId = route.params.partner_org_id;
  return paramPartnerId ? Number(paramPartnerId) : null;
});

const { customPopsWithStatsAndSharing, allPopulations: standardPopulations } =
  usePopulations(partnerId.value as never);

const allPopulations = computed(() => [
  ...standardPopulations.value,
  ...customPopsWithStatsAndSharing.value,
]);

const population = computed(() => {
  return allPopulations.value.find((pop) => pop.id === dataShare.populationId);
});

const hasExistingOverrides = computed(() => {
  if (!population.value?.populationToPopulationSettings) return false;
  return population.value.populationToPopulationSettings.length > 0;
});

const options = computed(() => {
  const optionsWithoutOverride: DropdownOption[] = [
    {
      value: 'restore-default',
      display: 'Restore default sharing settings',
      classes: ['c-sharing-item__container-option'],
      disabled: isDefaultSettings.value || !hasDefaultSettings.value,
      tooltip: isDefaultSettings.value
        ? 'The default settings are already applied for this population'
        : !hasDefaultSettings.value
          ? 'No default sharing settings for this population'
          : 'Restore default sharing settings',
      iconType: ['fak', 'restore'],
    },
    {
      value: 'unshare',
      display: 'Unshare population',
      classes: ['c-sharing-item__container-option'],
      iconType: ['fak', 'x'],
    },
  ];
  const shouldShowEditMode = hasExistingOverrides.value;

  const overrideDisplayText = shouldShowEditMode
    ? 'Edit overrides'
    : 'Create an override';

  const overrideIconType = shouldShowEditMode ? 'edit' : 'add';

  const overrideOption: DropdownOption = {
    value: 'create-override',
    display: overrideDisplayText,
    classes: ['c-sharing-item__container-option'],
    iconType: ['fak', overrideIconType],
    disabled: !partnerPopulations.value.length || hasUnsavedChanges,
    tooltip: hasUnsavedChanges
      ? 'Save sharing level settings before creating an override'
      : undefined,
  };
  if (hasSharingOverrides.value) {
    optionsWithoutOverride.splice(1, 0, overrideOption);
  }
  return optionsWithoutOverride;
});

function onOptionSelected(option: { value: string }) {
  showDropdownContent.value = false;
  if (option.value === 'restore-default') {
    handleRestoreToDefault();
  } else if (option.value === 'create-override') {
    onCreateOverride();
  } else if (option.value === 'unshare') {
    handleChange({ visibility: SHARING_TYPES.HIDDEN });
  }
}

async function handleChange(changes: {
  visibility?: SharingType;
  presetId?: number;
}) {
  // Select existing preset
  const createFromDefault = isForPartner.value && dataShare.partnerId === null;
  const changeFn = createFromDefault ? createDataShare : updateDataShare;
  await changeFn({
    populationId: dataShare.populationId,
    visibility: changes.visibility ?? dataShare.visibility,
    presetId: changes.presetId ?? dataShare.presetId,
  });
}

function onCreateOverride() {
  showDropdownContent.value = false;
  emit('show-pop-overrides-modal');
}

// Helpers / statics

// Check if a route name exists in matched routes
function isRouteNameInMatched(routeName: string) {
  return route.matched?.map((r) => r.name).includes(routeName) || false;
}

const sourceIdParam = isGoogleOrCsvSource(String(dataShare.integrationType))
  ? dataShare.sourceId
  : undefined;

const actualFeedId = computed(() => {
  const isGoogleOrCsv = isGoogleOrCsvSource(String(dataShare.integrationType));
  if (isGoogleOrCsv && dataShare.sourceId) {
    const source = sourcesStore.getSourceById(dataShare.sourceId);
    return source?.feed_id ?? dataShare.feedId;
  }
  return dataShare.feedId;
});

function handleOpenPresetModal(presetId: number | undefined) {
  openModal({
    feedId: actualFeedId.value,
    sourceId: sourceIdParam,
    presetId,
    isForPartner: isForPartner.value,
    onSuccess: (newPresetId: number, _isNewPreset: boolean) => {
      handleChange({ presetId: newPresetId });
    },
  });
}

async function handleRestoreToDefault() {
  await resetDataShare(dataShare);
}

function getPresetSuffixIcon(presetId?: number) {
  if (!presetId) return null;

  // Find the preset option to check its type
  const presetOption = presetOptionsWithCustomFields.value
    .flatMap((group) => group.items)
    .find((item) => item.value === presetId);

  // Single share presets can always be edited (by partners)
  const isSingleSharePreset =
    presetOption?.presetType === 'single_share_preset';

  // Crossbeam Default preset can never be edited
  const isRecommendedPreset = presetOption?.presetType === 'crossbeam_default';

  const canEdit =
    !disabled.value &&
    (isSingleSharePreset ||
      (!isForPartner.value && hasPopulationsPermission)) &&
    !isRecommendedPreset;

  return ['far', canEdit ? 'pen' : 'eye'] as [BittsIconPrefix, string];
}
</script>

<style lang="pcss" scoped>
@reference "../../../../../../css/theme.css";
.c-sharing-settings-item {
  @apply flex flex-col items-start gap-16 p-16 md:flex-row md:items-center md:justify-between md:gap-10;
}
.c-sharing-item__container-option {
  @apply flex w-full items-center px-8 py-4;
  &:hover {
    @apply bg-neutral-background-weak rounded;
  }
}
</style>

<style lang="pcss">
@reference "../../../../../../css/theme.css";
.c-sharing-settings-item__sharing {
  &.bitts-select
    .ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    @apply h-24 border-none rounded-[28px] shadow-none bg-info-background-weak;
  }
}
.c-sharing-settings-item__sharing--overlap-counts {
  &.bitts-select
    .ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    @apply bg-warning-background-weak;
  }
}
.c-sharing-settings-item__sharing--dropdown {
  @apply overflow-x-hidden;
  .ant-select-item {
    @apply w-[300px] p-10 flex items-center;
  }
}
.c-sharing-settings-item__sharing-option-description {
  @apply text-neutral-text-weak text-sm text-wrap;
}
.ant-select-selection-item {
  .c-sharing-settings-item__sharing-option-description,
  .c-sharing-settings-item__preset-details-btn {
    @apply hidden;
  }
}

/* Option label styles in select button */
.ant-select-selection-item {
  .c-sharing-settings-item__sharing-option-label--overlaps {
    @apply text-warning-text;
  }
  .c-sharing-settings-item__sharing-option-label--not-overlaps {
    @apply text-info-text;
  }
}
/* Option label styles in select dropdown */
.c-sharing-settings-item__sharing--dropdown {
  .c-sharing-settings-item__sharing-option-label {
    @apply font-bold! text-neutral-text-strong!;
  }
}
.c-sharing-settings-item__partner-btn {
  &.c-bitts-btn-small {
    @apply p-4;
  }
}
</style>
